import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowSquareOut, Eye, FolderOpen, CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { fileExplorerService } from '../../../services/files/fileExplorerService.js';
import { HttpError } from '../../../services/http/httpClient.js';
import { clearAuthAndRedirect } from '../../../services/http/auth.js';
import FileExplorerBreadcrumb from '../components/FileExplorerBreadcrumb.jsx';
import FileExplorerToolbar from '../components/FileExplorerToolbar.jsx';
import FileExplorerTable from '../components/FileExplorerTable.jsx';
import FileExplorerGrid from '../components/FileExplorerGrid.jsx';
import FileExplorerErrorState from '../components/FileExplorerErrorState.jsx';
import FileExplorerSelectionBar from '../components/FileExplorerSelectionBar.jsx';
import FileExplorerMaintenanceModal from '../components/FileExplorerMaintenanceModal.jsx';
import FileExplorerSearchResults from '../components/FileExplorerSearchResults.jsx';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog.jsx';
import FilePreviewModal from '../../../shared/components/filePreview/FilePreviewModal.jsx';
import {
  downloadFromUrl,
  getStorageFileProxyUrl,
  getTaskDocumentDownloadUrl,
  getTaskIdFromFolderId,
  openPdfInNewTab,
  resolvePreviewType
} from '../../../shared/utils/filePreview.js';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';

const DEFAULT_SORT_BY = 'size';
const DEFAULT_SORT_ORDER = 'desc';
const DEFAULT_PAGE_SIZE = 5;
// Mínimo del backend para q en /search: por debajo responde 400, no se llama.
const SEARCH_MIN_LENGTH = 2;
const DEFAULT_SEARCH_PAGE_SIZE = 20;

const mergeUniqueEntries = (currentEntries, newEntries) => {
  const entriesById = new Map(currentEntries.map((entry) => [entry.id, entry]));
  newEntries.forEach((entry) => entriesById.set(entry.id, entry));
  return Array.from(entriesById.values());
};

const mapErrorToState = (err) => {
  if (err instanceof HttpError && err.status === 404) {
    return { kind: 'not_found' };
  }

  if (err instanceof HttpError && err.status === 403) {
    return { kind: 'forbidden' };
  }

  return { kind: 'server' };
};

// 401 no llega acá: se redirige al login antes (ver efecto de búsqueda).
const mapSearchErrorToState = (err) => {
  if (err instanceof HttpError && err.status === 400) {
    return { message: err.message, retryable: false };
  }

  if (err instanceof HttpError && err.status === 403) {
    return { message: 'No tienes permiso para buscar archivos.', retryable: false };
  }

  return { message: 'No se pudo completar la búsqueda, inténtalo de nuevo.', retryable: true };
};

// parentFolderId de un resultado: "task:<taskId>" o "field:<taskId>:<campo>".
// El campo puede contener ":", por eso se toma todo lo que sigue al taskId.
const getFieldNameFromFolderId = (folderId) => {
  const [kind, , ...fieldParts] = String(folderId || '').split(':');
  return kind === 'field' ? fieldParts.join(':') : null;
};

/**
 * onViewTask(taskId, location): abre el detalle de la tarea; location
 * ({ folderId, breadcrumbTrail, search }) es dónde estaba el usuario, para
 * que el "volver" del detalle lo traiga de nuevo acá.
 * initialLocation: esa misma location al volver, para restaurar la vista.
 */
const FileExplorerScreen = ({ userRole, onViewTask, initialLocation = null }) => {
  const canManageFiles = userRole === 'admin';
  const [currentFolderId, setCurrentFolderId] = useState(initialLocation?.folderId ?? null);
  // Construido localmente a partir de los clicks del usuario, no del campo
  // `breadcrumb` de la respuesta: el backend lo devuelve siempre vacío ([])
  // incluso dentro de una carpeta de tarea, pese a lo documentado en el
  // contrato — ver nota en implementation-plan.md. Cada entrada guarda
  // { id, name } de la carpeta clickeada, en orden de navegación.
  const [breadcrumbTrail, setBreadcrumbTrail] = useState(initialLocation?.breadcrumbTrail ?? []);
  const [entries, setEntries] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [search, setSearch] = useState(initialLocation?.search ?? '');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotalItems, setSearchTotalItems] = useState(0);
  const [searchPageCurrent, setSearchPageCurrent] = useState(0);
  const [searchPageSize, setSearchPageSize] = useState(DEFAULT_SEARCH_PAGE_SIZE);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchReloadKey, setSearchReloadKey] = useState(0);
  // Se incrementa para limpiar el input del toolbar desde acá (ver resetKey).
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [pageCurrent, setPageCurrent] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  // Tamaño de página real que aplicó el backend a la primera tanda de la
  // grilla (viene de la respuesta, no se hardcodea en el front: el tope de
  // 20 es una decisión del backend y podría cambiar sin aviso). Se usa para
  // pedir las tandas siguientes con el mismo tamaño.
  const [gridPageSize, setGridPageSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMoreGrid, setLoadingMoreGrid] = useState(false);
  const [error, setError] = useState(null);

  // Map<id, entry> en vez de un array/Set de ids: para armar el body de
  // DELETE/download hace falta type/source de cada entry, y para el mensaje
  // de confirmación el fileCount de las carpetas — todo eso ya vino en el
  // entry con el listado, no hay que volver a pedirlo.
  const [selectedEntries, setSelectedEntries] = useState(() => new Map());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  // { file, inlineUrl, fetchUrl, downloadUrl } del archivo abierto en el visor.
  const [previewFile, setPreviewFile] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const fetchFolder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fileExplorerService.getFileExplorer({
        folderId: currentFolderId ?? undefined,
        sortBy,
        sortOrder,
        // La grilla no muestra controles de paginación, pero el backend igual
        // pagina (aunque se omitan estos parámetros): se pide la primera
        // tanda sin fijar un tamaño; el resto se trae con handleLoadMoreGrid,
        // concatenando en vez de reemplazar.
        ...(viewMode === 'list' ? { pageCurrent, pageSize } : {})
      });

      const responses = response.responses || [];
      setEntries(responses);
      setTotalItems(response.pagination?.totalItems || 0);
      if (viewMode === 'grid') {
        setGridPageSize(response.pagination?.pageSize || responses.length || null);
      }
    } catch (err) {
      setEntries([]);
      setTotalItems(0);
      setError(mapErrorToState(err));
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, sortBy, sortOrder, pageCurrent, pageSize, viewMode]);

  useEffect(() => {
    fetchFolder();
  }, [fetchFolder]);

  const trimmedSearch = search.trim();
  const isSearching = trimmedSearch.length >= SEARCH_MIN_LENGTH;

  // Cada búsqueda nueva aborta la anterior (cleanup del efecto), así una
  // respuesta vieja no pisa a la nueva.
  useEffect(() => {
    if (!isSearching) {
      return undefined;
    }

    const controller = new AbortController();

    const runSearch = async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const response = await fileExplorerService.searchFileExplorer({
          q: trimmedSearch,
          sortBy,
          sortOrder,
          pageCurrent: searchPageCurrent,
          pageSize: searchPageSize,
          signal: controller.signal
        });

        setSearchResults(response.responses || []);
        setSearchTotalItems(response.pagination?.totalItems || 0);
      } catch (err) {
        if (controller.signal.aborted) return;

        if (err instanceof HttpError && err.status === 401) {
          clearAuthAndRedirect();
          return;
        }

        setSearchResults([]);
        setSearchTotalItems(0);
        setSearchError(mapSearchErrorToState(err));
      } finally {
        if (!controller.signal.aborted) {
          setSearchLoading(false);
        }
      }
    };

    runSearch();

    return () => controller.abort();
  }, [isSearching, trimmedSearch, sortBy, sortOrder, searchPageCurrent, searchPageSize, searchReloadKey]);

  // pageCurrent se deduce de `entries.length` sobre el tamaño de página real
  // (gridPageSize, conocido recién tras la primera respuesta) en vez de
  // llevar un contador de página aparte que se pueda desincronizar.
  const handleLoadMoreGrid = async () => {
    if (loadingMoreGrid || !gridPageSize) return;

    setLoadingMoreGrid(true);
    try {
      const nextPageCurrent = Math.floor(entries.length / gridPageSize);
      const response = await fileExplorerService.getFileExplorer({
        folderId: currentFolderId ?? undefined,
        sortBy,
        sortOrder,
        pageCurrent: nextPageCurrent,
        pageSize: gridPageSize
      });

      setEntries((prev) => mergeUniqueEntries(prev, response.responses || []));
      setTotalItems(response.pagination?.totalItems || 0);
    } catch (err) {
      setError(mapErrorToState(err));
    } finally {
      setLoadingMoreGrid(false);
    }
  };

  const handleFolderClick = (entry) => {
    setPageCurrent(0);
    setSelectedEntries(new Map());
    setBreadcrumbTrail((prev) => [...prev, { id: entry.id, name: entry.name }]);
    setCurrentFolderId(entry.id);
  };

  // Imágenes (source 'image') se sirven por el proxy de storage; documentos
  // (source 'document') por el endpoint de la tarea. Los resultados de
  // búsqueda traen taskId; los entries del listado no, y sale del folderId
  // actual ("task:<taskId>").
  const handleFileOpen = (entry) => {
    if (entry.source === 'image') {
      const proxyUrl = getStorageFileProxyUrl(entry.id);
      setPreviewFile({ file: entry, inlineUrl: proxyUrl, fetchUrl: proxyUrl, downloadUrl: proxyUrl });
      return;
    }

    const taskId = entry.taskId ?? getTaskIdFromFolderId(currentFolderId);
    if (!taskId) {
      showToast('No se pudo identificar la tarea del documento.', 'error');
      return;
    }

    const inlineUrl = getTaskDocumentDownloadUrl(taskId, entry.id, { inline: true });
    if (resolvePreviewType(entry) === 'pdf') {
      openPdfInNewTab(inlineUrl);
      return;
    }

    const downloadUrl = getTaskDocumentDownloadUrl(taskId, entry.id);
    setPreviewFile({ file: entry, inlineUrl, fetchUrl: downloadUrl, downloadUrl });
  };

  // En modo selección, clickear una carpeta selecciona en vez de navegar —
  // evita el caso reportado de terminar dentro de una subcarpeta al intentar
  // tildar varios elementos.
  const handleEntryRowClick = (entry) => {
    if (isSelectionMode) {
      handleToggleEntry(entry);
      return;
    }
    if (entry.type === 'folder') {
      handleFolderClick(entry);
      return;
    }
    handleFileOpen(entry);
  };

  const handleClosePreview = useCallback(() => setPreviewFile(null), []);

  const handleViewTask = (taskId) => {
    onViewTask(taskId, { folderId: currentFolderId, breadcrumbTrail, search });
  };

  // Acciones por fila al navegar a mano (sin búsqueda). Las carpetas llevan el
  // taskId en su propio id ("task:<id>" / "field:<id>:<campo>"); los archivos,
  // en el de la carpeta actual.
  const getEntryActions = (entry) => {
    const taskId = getTaskIdFromFolderId(entry.type === 'folder' ? entry.id : currentFolderId);

    return [
      entry.type === 'folder'
        ? { label: 'Abrir', icon: <FolderOpen size={16} />, onClick: () => handleFolderClick(entry) }
        : { label: 'Vista previa', icon: <Eye size={16} />, onClick: () => handleFileOpen(entry) },
      {
        label: 'Ir a la tarea',
        icon: <ArrowSquareOut size={16} />,
        onClick: () => handleViewTask(taskId),
        disabled: !taskId || !onViewTask
      }
    ];
  };

  // El breadcrumb se arma localmente (ver breadcrumbTrail), así que al saltar
  // desde un resultado se reconstruye: tarea y, si es una imagen, su campo.
  const handleOpenResultFolder = (result) => {
    const taskCrumb = { id: `task:${result.taskId}`, name: result.taskTitle };
    const fieldName = getFieldNameFromFolderId(result.parentFolderId);

    setPageCurrent(0);
    setSelectedEntries(new Map());
    setBreadcrumbTrail(fieldName ? [taskCrumb, { id: result.parentFolderId, name: fieldName }] : [taskCrumb]);
    setCurrentFolderId(result.parentFolderId);
    setSearch('');
    setSearchResetKey((prev) => prev + 1);
  };


  const handleBreadcrumbClick = (crumbId) => {
    setPageCurrent(0);
    setSelectedEntries(new Map());
    if (crumbId === null) {
      setBreadcrumbTrail([]);
    } else {
      setBreadcrumbTrail((prev) => {
        const index = prev.findIndex((crumb) => crumb.id === crumbId);
        return index === -1 ? prev : prev.slice(0, index + 1);
      });
    }
    setCurrentFolderId(crumbId);
  };

  // "Seleccionar todo" solo actúa sobre `entries` (lo cargado/visible en este
  // momento — la página actual en list, lo acumulado por "Cargar más" en
  // grid), no sobre todo el contenido de la carpeta.
  const selectedIds = useMemo(() => new Set(selectedEntries.keys()), [selectedEntries]);
  const allVisibleSelected = entries.length > 0 && entries.every((entry) => selectedEntries.has(entry.id));

  const handleToggleEntry = (entry) => {
    setSelectedEntries((prev) => {
      const next = new Map(prev);
      if (next.has(entry.id)) {
        next.delete(entry.id);
      } else {
        next.set(entry.id, entry);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedEntries((prev) => {
      const next = new Map(prev);
      if (allVisibleSelected) {
        entries.forEach((entry) => next.delete(entry.id));
      } else {
        entries.forEach((entry) => next.set(entry.id, entry));
      }
      return next;
    });
  };

  const handleClearSelection = () => setSelectedEntries(new Map());

  // El toggle es la única puerta de entrada/salida del modo selección: al
  // salir se limpia la selección para que no quede "colgada" la próxima vez
  // que se active (p. ej. tras cambiar de carpeta).
  const handleToggleSelectionMode = () => {
    setIsSelectionMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedEntries(new Map());
      }
      return next;
    });
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  };

  const mapActionErrorToMessage = (err, accion) => {
    if (err instanceof HttpError) {
      if (err.status === 403) {
        return 'No tienes permiso para realizar esta acción.';
      }
      if (err.status === 404) {
        return 'Alguno de los elementos seleccionados ya no existe. Actualizando la lista...';
      }
      if (err.status === 400) {
        return `No se pudo ${accion}: la selección es inválida o supera el límite permitido por operación.`;
      }
    }
    return `Ocurrió un error inesperado al intentar ${accion}. Inténtalo de nuevo.`;
  };

  const selectedEntryList = useMemo(() => Array.from(selectedEntries.values()), [selectedEntries]);
  const estimatedFileCount = useMemo(
    () => selectedEntryList.reduce((sum, entry) => sum + (entry.type === 'folder' ? (entry.fileCount || 0) : 1), 0),
    [selectedEntryList]
  );

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const result = await fileExplorerService.deleteFileExplorerItems(selectedEntryList);
      const deletedCount = result?.deleted?.length || 0;
      const failedCount = result?.failed?.length || 0;

      if (failedCount > 0) {
        showToast(`Se eliminaron ${deletedCount} elemento(s); ${failedCount} no se pudo(eron) eliminar.`, 'warning');
      } else {
        showToast(`Se eliminaron ${deletedCount} elemento(s) correctamente.`, 'success');
      }

      setSelectedEntries(new Map());
      setShowDeleteConfirm(false);

      if (pageCurrent !== 0) {
        setPageCurrent(0);
      } else {
        fetchFolder();
      }
    } catch (err) {
      showToast(mapActionErrorToMessage(err, 'eliminar'), 'error');
      setShowDeleteConfirm(false);
      if (err instanceof HttpError && err.status === 404) {
        fetchFolder();
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadSelected = async () => {
    setDownloading(true);
    try {
      const response = await fileExplorerService.downloadFileExplorerItems(selectedEntryList);
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'archivos.zip';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err instanceof HttpError) {
        const payload = await err.response?.json().catch(() => null);
        showToast(payload?.message || mapActionErrorToMessage(err, 'descargar'), 'error');
        if (err.status === 404) {
          fetchFolder();
        }
      } else {
        // La conexión se cortó a mitad del stream del zip: se trata como
        // descarga fallida, nunca se dispara el <a download> con un blob truncado.
        showToast('La descarga se interrumpió. Inténtalo de nuevo.', 'error');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setPageCurrent(0);
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  };

  const handleSearchChange = (term) => {
    setSearchPageCurrent(0);
    setSearch(term);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const searchTotalPages = Math.max(1, Math.ceil(searchTotalItems / searchPageSize));

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Explorador de archivos"
        subtitle="Navega y busca los archivos almacenados en las tareas"
        icon={<FolderOpen size={28}/>}
      />
      <FileExplorerToolbar
        initialSearch={initialLocation?.search}
        sortBy={sortBy}
        sortOrder={sortOrder}
        resetKey={searchResetKey}
        viewMode={viewMode}
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        onViewModeChange={setViewMode}
        canSelect={canManageFiles && !isSearching}
        selectionMode={isSelectionMode}
        onToggleSelectionMode={handleToggleSelectionMode}
        onOpenMaintenance={() => setShowMaintenanceModal(true)}
      />
      {!isSearching && (
        <FileExplorerBreadcrumb items={breadcrumbTrail} onNavigate={handleBreadcrumbClick} />
      )}

      {canManageFiles && isSelectionMode && !error && !isSearching && (
        <FileExplorerSelectionBar
          totalVisible={entries.length}
          selectedCount={selectedEntries.size}
          allVisibleSelected={allVisibleSelected}
          onToggleSelectAll={handleToggleSelectAll}
          onClear={handleToggleSelectionMode}
          onDelete={() => setShowDeleteConfirm(true)}
          onDownload={handleDownloadSelected}
          deleting={deleting}
          downloading={downloading}
        />
      )}

      {isSearching ? (
        <FileExplorerSearchResults
          results={searchResults}
          loading={searchLoading}
          error={searchError}
          search={trimmedSearch}
          onRetry={() => setSearchReloadKey((prev) => prev + 1)}
          onPreview={handleFileOpen}
          onOpenFolder={handleOpenResultFolder}
          onViewTask={onViewTask ? handleViewTask : undefined}
          pagination={{
            currentPage: searchPageCurrent + 1,
            totalPages: searchTotalPages,
            pageSize: searchPageSize,
            totalItems: searchTotalItems,
            pageSizeOptions: [10, 20, 50],
            onPageChange: (page) => setSearchPageCurrent(page - 1),
            onPageSizeChange: (size) => {
              setSearchPageSize(size);
              setSearchPageCurrent(0);
            }
          }}
        />
      ) : error ? (
        <FileExplorerErrorState
          kind={error.kind}
          onAction={error.kind === 'not_found' ? () => handleBreadcrumbClick(null) : fetchFolder}
        />
      ) : viewMode === 'grid' ? (
        <FileExplorerGrid
          entries={entries}
          loading={loading}
          onRowClick={handleEntryRowClick}
          totalItems={totalItems}
          hasMore={entries.length < totalItems}
          loadingMore={loadingMoreGrid}
          onLoadMore={handleLoadMoreGrid}
          selectable={canManageFiles && isSelectionMode}
          selectedIds={selectedIds}
          onToggleEntry={handleToggleEntry}
          getEntryActions={getEntryActions}
        />
      ) : (
        <FileExplorerTable
          entries={entries}
          loading={loading}
          onRowClick={handleEntryRowClick}
          selectable={canManageFiles && isSelectionMode}
          selectedIds={selectedIds}
          onToggleEntry={handleToggleEntry}
          getEntryActions={getEntryActions}
          pagination={{
            currentPage: pageCurrent + 1,
            totalPages,
            pageSize,
            totalItems,
            pageSizeOptions: [5, 10, 20],
            onPageChange: (page) => setPageCurrent(page - 1),
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPageCurrent(0);
            }
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Eliminar elementos"
        message={`¿Eliminar ${selectedEntryList.length} elemento(s) seleccionado(s)? Esto afecta aproximadamente ${estimatedFileCount} archivo(s) — el contenido de las carpetas seleccionadas se elimina por completo. Esta acción no se puede deshacer.`}
        confirmText={deleting ? 'Eliminando...' : 'Eliminar'}
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <FilePreviewModal
        isOpen={Boolean(previewFile)}
        file={previewFile?.file}
        inlineUrl={previewFile?.inlineUrl}
        fetchUrl={previewFile?.fetchUrl}
        onDownload={() => downloadFromUrl(previewFile.downloadUrl, previewFile.file.name)}
        onClose={handleClosePreview}
      />

      <FileExplorerMaintenanceModal
        isOpen={showMaintenanceModal}
        onClose={() => setShowMaintenanceModal(false)}
        onDeleted={() => {
          setSelectedEntries(new Map());
          fetchFolder();
        }}
        onNotify={showToast}
      />

      {toast.show && (
        <div
          className={`fixed top-4 right-4 z-10000 p-4 rounded-lg shadow-lg ${
            toast.type === 'success'
              ? 'bg-success/12 border border-success text-success'
              : toast.type === 'warning'
                ? 'bg-warning/12 border border-warning text-warning'
                : 'bg-error/12 border border-error text-error'
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === 'success' ? (
              <CheckCircle size={20} weight="bold" />
            ) : toast.type === 'warning' ? (
              <WarningCircle size={20} weight="bold" />
            ) : (
              <XCircle size={20} weight="bold" />
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileExplorerScreen;
